import { useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { getAccountsLite, getAssetDetails, AssetDetailsResponse } from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';
import {
  loadAssetsForPlant,
  loadPlantsForSite,
  loadSitesForCustomer,
  mapCustomerAccountsToLite,
} from './assetSummaryDetailsHelpers';

type AssetDetails = AssetDetailsResponse;

type UseAssetSummaryFiltersParams = {
  assetId?: string;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
};

export function useAssetSummaryFilters({ assetId, navigate, searchParams }: UseAssetSummaryFiltersParams) {
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [sites, setSites] = useState<{ siteId: string; site: string }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [plants, setPlants] = useState<{ plantId: string; plant: string }[]>([]);
  const [selectedPlantId, setSelectedPlantId] = useState<string>('');
  const [assets, setAssets] = useState<{ id: string; assetId: string; assetName: string }[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [selectedAssetRecordId, setSelectedAssetRecordId] = useState<string>('');
  const [assetDetails, setAssetDetails] = useState<AssetDetails | null>(null);
  const [loadingAssetDetails, setLoadingAssetDetails] = useState<boolean>(false);
  const [lastCommunication, setLastCommunication] = useState<string | null>(null);

  const resetDependentSelections = () => {
    setSelectedSiteId('');
    setSelectedPlantId('');
    setSelectedAssetId('');
    setSelectedAssetRecordId('');
    setAssetDetails(null);
    setSites([]);
    setPlants([]);
    setAssets([]);
  };

  const refreshSitesForCustomer = async (customerId: string) => {
    try {
      setSites(await loadSitesForCustomer(customerId));
    } catch {
      setSites([]);
    }
  };

  const refreshPlantsForSite = async (siteId: string) => {
    try {
      setPlants(await loadPlantsForSite(siteId));
    } catch {
      setPlants([]);
    }
  };

  const refreshAssetsForPlant = async (plantId: string) => {
    if (!selectedCustomerId || !selectedSiteId) {
      setAssets([]);
      return;
    }
    try {
      setAssets(await loadAssetsForPlant(selectedCustomerId, selectedSiteId, plantId));
    } catch {
      setAssets([]);
    }
  };

  const handleCustomerChange = (newCustomerId: string) => {
    if (selectedCustomerId === newCustomerId) {
      resetDependentSelections();
      refreshSitesForCustomer(newCustomerId);
      return;
    }
    setSelectedCustomerId(newCustomerId);
    resetDependentSelections();
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const customerAccountsData = await getAccountsLite();
        if (!Array.isArray(customerAccountsData)) {
          setCustomers([]);
          return;
        }
        setCustomers(mapCustomerAccountsToLite(customerAccountsData));
      } catch {
        setCustomers([]);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchSites = async () => {
      if (!selectedCustomerId) {
        setSites([]);
        return;
      }
      try {
        setSites(await loadSitesForCustomer(selectedCustomerId));
      } catch {
        setSites([]);
      }
    };
    fetchSites();
    if (!searchParams.get('customerId')) {
      setPlants([]);
      setSelectedPlantId('');
    }
  }, [selectedCustomerId, searchParams]);

  useEffect(() => {
    if (!selectedSiteId) return;
    refreshPlantsForSite(selectedSiteId);
  }, [selectedSiteId]);

  useEffect(() => {
    if (!selectedPlantId) {
      setAssets([]);
      return;
    }
    refreshAssetsForPlant(selectedPlantId);
  }, [selectedPlantId, selectedCustomerId, selectedSiteId]);

  useEffect(() => {
    const fetchAssetDetails = async () => {
      if (!selectedAssetRecordId) {
        setAssetDetails(null);
        setLastCommunication(null);
        return;
      }

      setLoadingAssetDetails(true);
      try {
        const details = await getAssetDetails(selectedAssetRecordId);
        setAssetDetails(details);
        setLastCommunication(details.lastCommunication || null);

        if (details?.assetName) {
          const currentState = window.history.state || {};
          window.history.replaceState(
            {
              ...currentState,
              assetName: details.assetName,
              breadcrumbTitle: details.assetName,
            },
            '',
            window.location.pathname + window.location.search
          );
        }
      } catch {
        setAssetDetails(null);
        setLastCommunication(null);
      } finally {
        setLoadingAssetDetails(false);
      }
    };

    fetchAssetDetails();
  }, [selectedAssetRecordId]);

  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const siteId = searchParams.get('siteId');
    const plantId = searchParams.get('plantId');
    const selectedAsset = searchParams.get('selectedAssetId');

    if (customerId) setSelectedCustomerId(customerId);
    if (siteId) setSelectedSiteId(siteId);
    if (plantId) setSelectedPlantId(plantId);
    if (selectedAsset) setSelectedAssetId(selectedAsset);
  }, [searchParams]);

  useEffect(() => {
    if (!selectedAssetId || assets.length === 0) return;
    const selectedAsset = assets.find((asset) => asset.assetId === selectedAssetId);
    if (selectedAsset) setSelectedAssetRecordId(selectedAsset.id);
  }, [selectedAssetId, assets]);

  return {
    customers,
    sites,
    plants,
    assets,
    selectedCustomerId,
    selectedSiteId,
    selectedPlantId,
    selectedAssetId,
    selectedAssetRecordId,
    assetDetails,
    loadingAssetDetails,
    lastCommunication,
    handleCustomerChange,
    refreshPlantsForSite,
    refreshAssetsForPlant,
    setSelectedSiteId,
    setSelectedPlantId,
    setSelectedAssetId,
    setSelectedAssetRecordId,
    setAssetDetails,
    setPlants,
    setAssets,
    navigate,
    assetId,
  };
}
